<script setup lang="ts">
import {Head, router, useForm} from "@inertiajs/vue3";
import AuthLayout from "@/layouts/AuthLayout.vue";
import {route} from "ziggy-js";
import VFieldLabel from "@/components/forms/VFieldLabel.vue";
import {computed, ref} from "vue";

defineOptions({layout: AuthLayout})

const iconMapper = {
    password: 'mdi-eye-outline',
    text: 'mdi-eye-off-outline',
}

const fieldType = ref<'password'|'text'>('password')

const fieldIcon = computed(() => {
    return iconMapper[fieldType.value]
})

const form = useForm({
    email: '',
    password: '',
    remember: true,
})

const switchFieldType = () => {
    fieldType.value = fieldType.value === 'password' ? 'text' : 'password'
}

const submit = () => {
    form.post(route('login.store'), {
        preserveScroll: true,
        onFinish: () => {
            form.reset('password')
        },
    })
}

const forgotPassword = () => {
    router.visit(route('password.request', {
        email: form.email,
    }))
}

const register = () => {
    router.visit(route('register'))
}
</script>

<template>
    <Head title="Log in ⋅ Capacity Framework Portal" />
    <v-card
        variant="flat"
        class="text-center"
    >
        <v-card-title class="text-center text-h4 text-pre-wrap">
            <p class="text-h5 text-medium-emphasis">
                Welcome to the
            </p>
            <p>
                Capacity Framework Portal
            </p>
        </v-card-title>
        <v-card-actions class="flex-column mb-3">
            <v-btn
                variant="outlined"
                color="base"
                border="sm"
                block
                :href="route('auth.redirect')"
            >
                <template #prepend>
                    <img
                        src="@/assets/google-logo.svg"
                        width="20"
                        alt="Google Logo"
                    >
                </template>
                <div class="text-body-1">
                    Continue with Google
                </div>
            </v-btn>
        </v-card-actions>
    </v-card>
    <v-divider class="my-1">
        <span class="text-body-2">or</span>
    </v-divider>
    <v-card
        variant="flat"
        class="justify-center"
    >
        <v-form
            @submit.prevent="submit"
        >
            <v-alert
                v-if="form.hasErrors"
                variant="tonal"
                type="error"
                :icon="false"
                class="text-center text-body-2 mt-5"
            >
                {{ form.errors.email || form.errors.password }}
            </v-alert>
            <v-card-text
                class="mt-0 px-2"
            >
                <v-field-label>
                    Email address
                </v-field-label>
                <v-text-field
                    v-model="form.email"
                    type="email"
                    name="email"
                    autocomplete="email"
                    class="mb-3"
                    hide-details
                    autofocus
                />
                <v-field-label>
                    Password
                </v-field-label>
                <v-text-field
                    v-model="form.password"
                    :type="fieldType"
                    :append-inner-icon="fieldIcon"
                    name="password"
                    autocomplete="password"
                    hide-details
                    @click:append-inner="switchFieldType"
                />
                <div
                    class="text-end my-2 text-primary forgot-password-link custom-link"
                    @click="forgotPassword"
                >
                    Forgot password?
                </div>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    variant="flat"
                    type="submit"
                    :loading="form.processing"
                    block
                >
                    Log in
                </v-btn>
            </v-card-actions>
        </v-form>
        <p class="text-body-2 text-center mt-3">
            No account? <span
                class="text-primary custom-link"
                @click="register"
            >
                Create one
            </span>
        </p>
        <p class="text-body-2 text-center mt-3 mb-3 text-disabled px-5 text-just">
            This resource is supported as a whole or in part by the National Institute Of Allergy And Infectious Diseases of the National Institutes of Health under grant n°<a
                href="https://reporter.nih.gov/search/JHbtQB-XeUu8wvs3vUlFFw/project-details/10913643?utm_source=capacity_framework_portal"
                target="_blank"
                class="text-primary"
            >U24AI183840</a> - <a
                href="https://pathogendatanetwork.org?utm_source=capacity_framework_portal"
                target="_blank"
                class="text-primary"
            >Pathogen Data Network</a>.
        </p>
    </v-card>
</template>

<style scoped>
.forgot-password-link {
    font-size: 0.725rem;
}
</style>
